/**
 * /accounts/:id — per-account rules view + editor.
 *
 * The wizard's step-rules confirms the rules once; this page lets the
 * operator edit them any time after. Same fields, different semantics:
 * PATCHes without `confirm: true` (keeps rules_confirmed_at pinned to
 * the original confirmation, bumps rules_edited_at). No auto-pair
 * attempt here — pairing already happened at confirm time, or didn't.
 */

import { Link, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Link2, Check, X as XIcon, AlertTriangle } from "@/components/ui/icons";
import {
  advanceAccountPhase,
  decodeAccountInfo,
  getAccount,
  getAccountRules,
  getAccountSnapshots,
  getAccountTrades,
  markAccountBreached,
  markAccountPassed,
  patchAccountCredentials,
  patchAccountRules,
  refetchAccountHistory,
  resumeAccount,
  setAccountLabel,
  validateAccount,
  type AccountRow,
  type AccountRulesRes,
  type AccountSnapshot,
  type AccountTrade,
} from "@/lib/api/accounts";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { extractApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  DataTableShell,
  DataTableHeader,
  DataTableRow,
  DataTableCell,
} from "@/components/ui/data-table";
import { FirmAvatar } from "@/components/firm-logo";
import { firmLogoUrl } from "@/lib/firm-logos";
import { ReassignBotDialog } from "@/components/reassign-bot-dialog";
import { cn } from "@/lib/utils";

export function AccountDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [account, setAccount] = useState<AccountRow | null>(null);
  const [rules, setRules] = useState<AccountRulesRes | null>(null);
  const [trades, setTrades] = useState<AccountTrade[] | null>(null);
  const [snapshots, setSnapshots] = useState<AccountSnapshot[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    const [acc, r, t, s] = await Promise.all([
      getAccount(id),
      getAccountRules(id),
      getAccountTrades(id).catch(() => [] as AccountTrade[]),
      getAccountSnapshots(id).catch(() => [] as AccountSnapshot[]),
    ]);
    setAccount(acc);
    setRules(r);
    setTrades(t);
    setSnapshots(s);
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await refetch();
        if (cancelled) return;
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refetch]);

  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }
  if (!account || !rules) {
    return <div className="p-4 text-sm text-muted-foreground sm:p-6">Loading…</div>;
  }

  const info = decodeAccountInfo(account);
  const logoSrc = firmLogoUrl(account.firm_id) ?? "";
  const isTerminal = account.outcome != null;

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 p-4 sm:p-6">
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <Link to="/accounts" className="inline-flex items-center gap-1.5 hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> All Accounts
        </Link>
      </div>

      {/* ── Header card ─────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-border/40 bg-muted/10 p-4 sm:p-5">
        <FirmAvatar src={logoSrc} name={account.firm_id} size="lg" className="ring-1 ring-border/40" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="truncate text-lg font-medium text-foreground">
              {info?.broker || account.firm_id}
            </div>
            <LabelEditor
              accountId={id}
              initial={account.account_label}
              onSaved={(label) => setAccount({ ...account, account_label: label })}
            />
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="truncate font-mono">{account.id}</span>
            <span>·</span>
            <span>{account.platform_type}</span>
            <span>·</span>
            <StatusBadge
              status={account.status}
              outcome={account.outcome}
            />
            <span>·</span>
            <span className="uppercase tracking-wider">{account.phase}</span>
          </div>
        </div>
        <div className="grid w-full grid-cols-2 gap-px overflow-hidden rounded-lg border border-border/40 bg-border/40 text-right sm:w-auto sm:min-w-[260px]">
          <div className="bg-card px-3 py-2">
            <div className="font-sans text-xs font-medium capitalize tracking-wide text-muted-foreground">
              Balance
            </div>
            <div className="mt-0.5 font-mono text-sm font-semibold text-accent">
              {info ? fmtMoney(info.balance, info.currency) : "—"}
            </div>
          </div>
          <div className="bg-card px-3 py-2">
            <div className="font-sans text-xs font-medium capitalize tracking-wide text-muted-foreground">
              Account #
            </div>
            <div className="mt-0.5 font-mono text-sm text-foreground">
              {info?.accountNumber || "—"}
            </div>
          </div>
        </div>
      </div>

      {/* ── Pair banner ─────────────────────────────────────────── */}
      {account.pair_slug ? (
        <Link
          to={`/pairs/${encodeURIComponent(account.pair_slug)}`}
          className="flex items-center gap-3 rounded-lg border border-accent/30 bg-accent/5 px-4 py-3 text-sm hover:bg-accent/10"
        >
          <Link2 className="h-4 w-4 text-accent" />
          <div className="flex-1">
            <div className="font-medium text-foreground">Paired</div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              Hedging against its counterpart. View the pair →
            </div>
          </div>
          <div className="font-mono text-xs text-muted-foreground">{account.pair_slug}</div>
        </Link>
      ) : (
        <div className="rounded-lg border border-dashed border-border/50 bg-muted/10 px-4 py-3 text-xs text-muted-foreground">
          Unpaired. We&rsquo;ll auto-pair this account when a compatible cross-firm account with
          the same size + phase + DD profile appears.
        </div>
      )}

      {/* ── Latest snapshot / terminal banner ───────────────────── */}
      <LatestSnapshotCard
        account={account}
        currency={info?.currency ?? "USD"}
        latest={snapshots?.[0] ?? null}
      />

      {/* ── Connection (URL + creds + re-validate) ──────────────── */}
      <ConnectionCard
        accountId={id}
        account={account}
        onChanged={refetch}
      />

      {/* ── Position history ────────────────────────────────────── */}
      <section>
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Position History</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Every trade this account has placed. Open positions stay here with
              an empty close.
            </p>
          </div>
          <RefetchHistoryButton accountId={id} onRefetched={refetch} />
        </div>
        <PositionHistoryTable
          trades={trades ?? []}
          currency={info?.currency ?? "USD"}
        />
      </section>

      {/* ── Lifecycle actions ───────────────────────────────────── */}
      {!isTerminal && (
        <LifecycleActions
          accountId={id}
          accountSlug={account.slug}
          assignedBotId={account.assigned_bot_id}
          status={account.status}
          phase={account.phase}
          steps={account.steps}
          challengeType={account.challenge_type}
          platformType={account.platform_type}
          currentAccountLabel={account.account_label}
          onDone={refetch}
        />
      )}

      {/* ── Rules editor ────────────────────────────────────────── */}
      <section>
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Challenge Rules</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              The AI obeys these. If they&rsquo;re wrong, fix them here — it&rsquo;s the
              canonical record for this account.
            </p>
          </div>
          <RulesTimestamps account={rules.account} />
        </div>
        <RulesEditor
          accountId={id}
          rules={rules}
          onSaved={(updated) =>
            setRules({ ...rules, account: { ...rules.account, ...updated, rulesEditedAt: Date.now() } })
          }
        />
      </section>
    </div>
  );
}

// ─── Rules editor (inline, mirrors step-rules form) ─────────────

type FormState = Record<string, string>;

/**
 * Pre-fill pattern: operator-confirmed values (account.*) WIN over the
 * firm-detected defaults (detected.*). A null in account means "we
 * never heard from the operator on this field" — fall back to detected
 * so the form shows the firm's published value, not an empty cell.
 */
function ruleToForm(
  r: AccountRulesRes["account"],
  d: AccountRulesRes["detected"] | null,
): FormState {
  const dd = (d ?? {}) as Record<string, unknown>;
  const pick = <K extends keyof AccountRulesRes["account"]>(
    key: K,
  ): AccountRulesRes["account"][K] | unknown => {
    const v = r[key];
    if (v !== null && v !== undefined) return v;
    return dd[key as string] ?? null;
  };
  const s = (v: unknown): string => (v === null || v === undefined ? "" : String(v));
  const b = (v: unknown): string => (v === true ? "yes" : v === false ? "no" : "unknown");
  return {
    accountSize: s(pick("accountSize")),
    challengeType: s(pick("challengeType")),
    steps: s(pick("steps")),
    profitTargetPct: s(pick("profitTargetPct")),
    profitTargetStep1Pct: s(pick("profitTargetStep1Pct") ?? pick("profitTargetPct")),
    profitTargetStep2Pct: s(pick("profitTargetStep2Pct")),
    profitTargetStep3Pct: s(pick("profitTargetStep3Pct")),
    dailyLossPct: s(pick("dailyLossPct")),
    dailyDdType: s(pick("dailyDdType")),
    maxDrawdownPct: s(pick("maxDrawdownPct")),
    maxDdType: s(pick("maxDdType")),
    profitSplitPct: s(pick("profitSplitPct")),
    payoutFrequency: s(pick("payoutFrequency")),
    leverage: s(pick("leverage")),
    leverageFx: s(pick("leverageFx") ?? pick("leverage")),
    leverageIndices: s(pick("leverageIndices")),
    leverageMetals: s(pick("leverageMetals")),
    leverageCommodities: s(pick("leverageCommodities")),
    leverageCrypto: s(pick("leverageCrypto")),
    minTradingDays: s(pick("minTradingDays")),
    weekendHoldingAllowed: b(pick("weekendHoldingAllowed")),
    newsTradingAllowed: b(pick("newsTradingAllowed")),
    consistencyRulePct: s(pick("consistencyRulePct")),
    maxRiskPerTradePct: s(pick("maxRiskPerTradePct")),
    maxLotSize: s(pick("maxLotSize")),
    payoutThresholdPct: s(pick("payoutThresholdPct")),
    minProfitableDays: s(pick("minProfitableDays")),
  };
}

function formToPatch(f: FormState): Record<string, unknown> {
  // `|| null` turned a legitimate 0 into null — a consistency rule of
  // "0%" (i.e. no consistency requirement) would get replaced by the
  // firm default every save. Parse explicitly and only null out on
  // empty-or-NaN.
  const n = (v: string): number | null => {
    const t = v.trim();
    if (t === "") return null;
    const x = Number(t);
    return Number.isNaN(x) ? null : x;
  };
  const i = (v: string): number | null => {
    const t = v.trim();
    if (t === "") return null;
    const x = parseInt(t, 10);
    return Number.isNaN(x) ? null : x;
  };
  const str = (v: string): string | null => (v.trim() === "" ? null : v.trim());
  const bool = (v: string): boolean | null => (v === "yes" ? true : v === "no" ? false : null);
  // Phase targets that don't apply to the current challenge type get
  // forced to null on save — otherwise stale values from a prior
  // challenge type round-trip back to D1 and confuse the AI prompt.
  const ct = (f.challengeType ?? "").toLowerCase();
  const phases = phaseCountFor(ct, f.steps);
  const phaseTarget = (phaseIdx: 1 | 2 | 3, raw: string): number | null =>
    phases >= phaseIdx ? n(raw) : null;
  return {
    accountSize: i(f.accountSize ?? ""),
    challengeType: str(f.challengeType ?? ""),
    steps: ct === "instant" ? 0 : i(f.steps ?? ""),
    profitTargetPct: phases >= 1 ? n(f.profitTargetStep1Pct || f.profitTargetPct || "") : null,
    profitTargetStep1Pct: phaseTarget(1, f.profitTargetStep1Pct ?? ""),
    profitTargetStep2Pct: phaseTarget(2, f.profitTargetStep2Pct ?? ""),
    profitTargetStep3Pct: phaseTarget(3, f.profitTargetStep3Pct ?? ""),
    dailyLossPct: n(f.dailyLossPct ?? ""),
    dailyDdType: str(f.dailyDdType ?? ""),
    maxDrawdownPct: n(f.maxDrawdownPct ?? ""),
    maxDdType: str(f.maxDdType ?? ""),
    profitSplitPct: n(f.profitSplitPct ?? ""),
    payoutFrequency: str(f.payoutFrequency ?? ""),
    leverage: str(f.leverage ?? ""),
    leverageFx: str(f.leverageFx ?? ""),
    leverageIndices: str(f.leverageIndices ?? ""),
    leverageMetals: str(f.leverageMetals ?? ""),
    leverageCommodities: str(f.leverageCommodities ?? ""),
    leverageCrypto: str(f.leverageCrypto ?? ""),
    minTradingDays: i(f.minTradingDays ?? ""),
    weekendHoldingAllowed: bool(f.weekendHoldingAllowed ?? ""),
    newsTradingAllowed: bool(f.newsTradingAllowed ?? ""),
    consistencyRulePct: n(f.consistencyRulePct ?? ""),
    maxRiskPerTradePct: n(f.maxRiskPerTradePct ?? ""),
    maxLotSize: n(f.maxLotSize ?? ""),
    payoutThresholdPct: n(f.payoutThresholdPct ?? ""),
    minProfitableDays: i(f.minProfitableDays ?? ""),
  };
}

function RulesEditor({
  accountId,
  rules,
  onSaved,
}: {
  accountId: string;
  rules: AccountRulesRes;
  onSaved: (updated: Record<string, unknown>) => void;
}) {
  const initial = useMemo(() => ruleToForm(rules.account, rules.detected), [rules]);
  const [form, setForm] = useState<FormState>(initial);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);

  const set = useCallback((k: string, v: string) => setForm((p) => ({ ...p, [k]: v })), []);

  const save = useCallback(async () => {
    if (saving || !dirty) return;
    setSaving(true);
    setMsg(null);
    try {
      const patch = formToPatch(form);
      await patchAccountRules(accountId, patch);
      onSaved(patch);
      setMsg("Saved");
    } catch (e) {
      setMsg(extractApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }, [accountId, form, onSaved, saving, dirty]);

  /** Save + stamp rules_confirmed_at in one shot. Without this stamp
   *  tryAutoPair treats the account as "rules not yet confirmed" and
   *  refuses to pair it — the same gate that blocks the rematch path. */
  const saveAndConfirm = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    setMsg(null);
    try {
      const patch = { ...formToPatch(form), confirm: true };
      await patchAccountRules(accountId, patch);
      onSaved(patch);
      setMsg("Confirmed — Account Is Pair-Eligible");
    } catch (e) {
      setMsg(extractApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }, [accountId, form, onSaved, saving]);

  const confirmed = rules.account.rulesConfirmedAt != null;
  const challengeType = (form.challengeType ?? "").toLowerCase();
  const phaseCount = phaseCountFor(challengeType, form.steps);
  const isInstant = challengeType === "instant";

  return (
    <div className="space-y-4 rounded-xl border border-border/40 bg-muted/5 p-4 sm:p-5">
      {dirty && (
        <Alert className="border-amber-500/40 bg-amber-500/10 py-3">
          <AlertDescription className="text-xs text-amber-200">
            Unsaved edits. The AI keeps using your last saved values until you save.
          </AlertDescription>
        </Alert>
      )}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Identity ─────────────────────────────────────── */}
        <Num label="Account Size (USD)" value={form.accountSize ?? ""} onChange={(v) => set("accountSize", v)} />
        <Sel label="Challenge Type" value={form.challengeType ?? ""} onChange={(v) => set("challengeType", v)} options={[
          { value: "", label: "—" },
          { value: "instant", label: "Instant funding" },
          { value: "1-step", label: "1-step" },
          { value: "2-step", label: "2-step" },
          { value: "3-step", label: "3-step" },
          { value: "evaluation", label: "Evaluation" },
          { value: "other", label: "Other" },
        ]} />
        {!isInstant && (
          <Num
            label="Challenge Phases (Total)"
            value={form.steps ?? ""}
            onChange={(v) => set("steps", v)}
          />
        )}

        {/* Phase profit targets — hidden for instant, otherwise
            renders only the steps that actually apply. */}
        {phaseCount > 0 && (
          <div className="md:col-span-2">
            <RuleGroupLabel
              title="Phase Profit Targets"
              hint={
                phaseCount === 1
                  ? "1-step challenge — single target gates the funded phase."
                  : `Fill the target for each of the ${phaseCount} evaluation phase${phaseCount > 1 ? "s" : ""}.`
              }
            />
            <div className={cn(
              "mt-3 grid grid-cols-1 gap-4",
              phaseCount === 1 ? "md:grid-cols-1" : phaseCount === 2 ? "md:grid-cols-2" : "md:grid-cols-3",
            )}>
              <Num label="Step 1 Target %" value={form.profitTargetStep1Pct ?? ""} onChange={(v) => set("profitTargetStep1Pct", v)} />
              {phaseCount >= 2 && (
                <Num label="Step 2 Target %" value={form.profitTargetStep2Pct ?? ""} onChange={(v) => set("profitTargetStep2Pct", v)} />
              )}
              {phaseCount >= 3 && (
                <Num label="Step 3 Target %" value={form.profitTargetStep3Pct ?? ""} onChange={(v) => set("profitTargetStep3Pct", v)} />
              )}
            </div>
          </div>
        )}

        {/* Drawdown ─────────────────────────────────────── */}
        <Num label="Daily Loss %" value={form.dailyLossPct ?? ""} onChange={(v) => set("dailyLossPct", v)} />
        <Sel label="Daily DD Type" value={form.dailyDdType ?? ""} onChange={(v) => set("dailyDdType", v)} options={[
          { value: "", label: "—" },
          { value: "eod", label: "EOD" },
          { value: "static", label: "Static" },
          { value: "intraday_trailing", label: "Intraday trailing" },
        ]} />
        <Num label="Max Drawdown %" value={form.maxDrawdownPct ?? ""} onChange={(v) => set("maxDrawdownPct", v)} />
        <Sel label="Max DD Type" value={form.maxDdType ?? ""} onChange={(v) => set("maxDdType", v)} options={[
          { value: "", label: "—" },
          { value: "static", label: "Static" },
          { value: "trailing", label: "Trailing" },
          { value: "trailing_from_initial", label: "Trailing from initial" },
        ]} />

        {/* Funded trader rules ──────────────────────────── */}
        <div className="md:col-span-2">
          <RuleGroupLabel
            title={isInstant ? "Funded Trader Rules" : "Funded Trader Rules (Post-Pass)"}
            hint={
              isInstant
                ? "Per-trade and per-payout constraints the firm enforces day one."
                : "These apply once the account reaches funded. Leave blank if the firm doesn't publish them."
            }
          />
          <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-3">
            <Num label="Max Risk / Trade %" value={form.maxRiskPerTradePct ?? ""} onChange={(v) => set("maxRiskPerTradePct", v)} />
            <Num label="Max Lot Size" value={form.maxLotSize ?? ""} onChange={(v) => set("maxLotSize", v)} />
            <Num label="Payout Threshold %" value={form.payoutThresholdPct ?? ""} onChange={(v) => set("payoutThresholdPct", v)} />
          </div>
        </div>

        {/* Payout & timing ──────────────────────────────── */}
        <Num label="Profit Split %" value={form.profitSplitPct ?? ""} onChange={(v) => set("profitSplitPct", v)} />
        <Txt label="Payout Frequency" value={form.payoutFrequency ?? ""} onChange={(v) => set("payoutFrequency", v)} />

        {/* Leverage ─────────────────────────────────────── */}
        <div className="md:col-span-2">
          <RuleGroupLabel
            title="Leverage By Symbol Category"
            hint="Different firms often use different leverage for FX, indices, metals, commodities, and crypto."
          />
          <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-3">
            <Txt label="FX Leverage" value={form.leverageFx ?? ""} onChange={(v) => set("leverageFx", v)} />
            <Txt label="Indices Leverage" value={form.leverageIndices ?? ""} onChange={(v) => set("leverageIndices", v)} />
            <Txt label="Metals Leverage" value={form.leverageMetals ?? ""} onChange={(v) => set("leverageMetals", v)} />
            <Txt label="Commodities Leverage" value={form.leverageCommodities ?? ""} onChange={(v) => set("leverageCommodities", v)} />
            <Txt label="Crypto Leverage" value={form.leverageCrypto ?? ""} onChange={(v) => set("leverageCrypto", v)} />
            <Txt label="Fallback Leverage" value={form.leverage ?? ""} onChange={(v) => set("leverage", v)} />
          </div>
        </div>

        {/* Day count + consistency ──────────────────────── */}
        <Num
          label={isInstant ? "Min Trading Days Before Payout" : "Min Trading Days In Phase"}
          value={form.minTradingDays ?? ""}
          onChange={(v) => set("minTradingDays", v)}
        />
        <Num label="Min Profitable Days" value={form.minProfitableDays ?? ""} onChange={(v) => set("minProfitableDays", v)} />
        <Num label="Consistency Rule %" value={form.consistencyRulePct ?? ""} onChange={(v) => set("consistencyRulePct", v)} />

        {/* Permissions ──────────────────────────────────── */}
        <Sel label="Weekend Holding" value={form.weekendHoldingAllowed ?? ""} onChange={(v) => set("weekendHoldingAllowed", v)} options={[
          { value: "unknown", label: "Unknown" },
          { value: "yes", label: "Allowed" },
          { value: "no", label: "Not allowed" },
        ]} />
        <Sel label="News Trading" value={form.newsTradingAllowed ?? ""} onChange={(v) => set("newsTradingAllowed", v)} options={[
          { value: "unknown", label: "Unknown" },
          { value: "yes", label: "Allowed" },
          { value: "no", label: "Restricted" },
        ]} />
      </div>
      <div className="flex flex-wrap items-center justify-end gap-3">
        <span className="text-xs text-muted-foreground">{msg ?? ""}</span>
        <Button disabled={!dirty || saving} onClick={save} size="sm" variant="outline">
          {saving ? "Saving…" : "Save Changes"}
        </Button>
        <Button disabled={saving} onClick={saveAndConfirm} size="sm">
          {saving
            ? "Saving…"
            : confirmed
              ? "Re-Confirm Rules"
              : "Confirm Rules — Make Pair-Eligible"}
        </Button>
      </div>
    </div>
  );
}

/**
 * How many phase profit-target inputs to render. `instant` has zero;
 * `1-step` / `2-step` / `3-step` are explicit; for `evaluation` /
 * `other` / unset, fall back to the numeric `steps` field, defaulting
 * to 2 when even that's blank (most common funnel).
 */
function phaseCountFor(challengeType: string, stepsRaw: string | undefined): number {
  if (challengeType === "instant") return 0;
  if (challengeType === "1-step") return 1;
  if (challengeType === "2-step") return 2;
  if (challengeType === "3-step") return 3;
  const n = parseInt((stepsRaw ?? "").trim(), 10);
  if (Number.isNaN(n)) return 2;
  if (n <= 0) return 0;
  if (n > 3) return 3;
  return n;
}

function Num(p: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <Field label={p.label}>
      <input
        type="text"
        inputMode="decimal"
        value={p.value}
        onChange={(e) => p.onChange(e.target.value.replace(/[^0-9.]/g, ""))}
        className="h-10 w-full rounded-md border border-border/40 bg-background px-3 text-sm"
      />
    </Field>
  );
}

function RuleGroupLabel(p: { title: string; hint: string }) {
  return (
    <div className="rounded-lg border border-border/40 bg-muted/20 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-foreground">
        {p.title}
      </p>
      <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
        {p.hint}
      </p>
    </div>
  );
}

function Txt(p: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <Field label={p.label}>
      <input
        type="text"
        value={p.value}
        onChange={(e) => p.onChange(e.target.value)}
        className="h-10 w-full rounded-md border border-border/40 bg-background px-3 text-sm"
      />
    </Field>
  );
}
function Sel(p: { label: string; value: string; onChange: (v: string) => void; options: Array<{ value: string; label: string }> }) {
  return (
    <Field label={p.label}>
      <select
        value={p.value}
        onChange={(e) => p.onChange(e.target.value)}
        className="h-10 w-full rounded-md border border-border/40 bg-background px-3 text-sm"
      >
        {p.options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </Field>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="font-sans text-xs font-medium capitalize tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}

function RulesTimestamps({ account }: { account: AccountRulesRes["account"] }) {
  const rel = (ms: number | null): string => {
    if (!ms) return "never";
    const diff = Date.now() - ms;
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
    return `${Math.round(diff / 86_400_000)}d ago`;
  };
  return (
    <div className="space-y-0.5 text-right font-sans text-xs text-muted-foreground">
      <div>Confirmed <span className="font-medium text-foreground">{rel(account.rulesConfirmedAt)}</span></div>
      <div>Edited <span className="font-medium text-foreground">{rel(account.rulesEditedAt)}</span></div>
    </div>
  );
}

function StatusBadge({
  status,
  outcome,
}: {
  status: string;
  outcome: "passed" | "breached" | null;
}) {
  // Terminal outcome wins over raw status so a passed / breached account
  // doesn't display as the generic "closed" it technically is in the DB.
  const label = outcome ?? status;
  const tone =
    outcome === "passed"
      ? "bg-emerald-500/10 text-emerald-500"
      : outcome === "breached"
      ? "bg-destructive/10 text-destructive"
      : status === "active"
      ? "bg-emerald-500/10 text-emerald-500"
      : status === "paused"
      ? "bg-amber-500/10 text-amber-500"
      : status === "failed_validation"
      ? "bg-destructive/10 text-destructive"
      : "bg-muted text-muted-foreground";
  return (
    <span className={cn("inline-flex rounded-full px-2 py-0.5 font-sans text-xs font-medium capitalize tracking-wide", tone)}>
      {label}
    </span>
  );
}

/**
 * Three-tile card showing the freshest equity / balance / open-PnL
 * snapshot on file. When the account has already been marked terminal
 * (outcome != null) we show a banner + use `final_equity/final_balance`
 * from the accounts row — the snapshot stream may have continued for a
 * moment after mark-passed, but what we care about is the number at the
 * moment the operator called it.
 */
// ─── Connection (terminal URL + creds + re-validate) ────────────
function ConnectionCard({
  accountId,
  account,
  onChanged,
}: {
  accountId: string;
  account: AccountRow;
  onChanged: () => Promise<void>;
}) {
  const [editOpen, setEditOpen] = useState(false);
  const [validating, setValidating] = useState(false);

  const status = account.status;
  const isFailed = status === "failed_validation";
  const isValidating = status === "validating";
  const isPending = status === "pending";
  const isTerminal = account.outcome != null;
  const hasOverride = account.login_url != null && account.login_url !== "";

  const onValidate = async () => {
    if (validating) return;
    setValidating(true);
    try {
      await validateAccount(accountId);
      toast.success("Validation triggered — bot is logging in now");
      await onChanged();
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setValidating(false);
    }
  };

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Connection</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            What the bot uses to log in. Edit any field if validation keeps
            failing — then click Validate to retry.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setEditOpen(true)}
            disabled={isTerminal}
          >
            Edit
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => void onValidate()}
            disabled={validating || isValidating || isTerminal}
          >
            {isValidating || validating
              ? "Validating…"
              : isPending
                ? "Validate"
                : "Re-Validate"}
          </Button>
        </div>
      </div>

      {isFailed && account.validation_error && (
        <Alert variant="destructive" className="mb-3">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <span className="font-medium">Last validation failed:</span>{" "}
            {account.validation_error}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-border/40 bg-border/40 sm:grid-cols-2">
        <ConnectionField
          label="Terminal URL"
          value={account.login_url}
          empty={
            hasOverride
              ? null
              : "Using firm-platform default (no override)."
          }
          mono
        />
        <ConnectionField label="Login" value={account.login} mono />
        <ConnectionField
          label="Broker Server"
          value={account.broker_server}
          mono
          empty="Inferred from firm default."
        />
        <ConnectionField
          label="Password"
          value={account.password ? "••••••••" : null}
          empty="Not set."
        />
      </div>

      <EditConnectionDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        accountId={accountId}
        initial={{
          loginUrl: account.login_url ?? "",
          login: account.login ?? "",
          password: "",
          brokerServer: account.broker_server ?? "",
        }}
        onSaved={async () => {
          setEditOpen(false);
          await onChanged();
        }}
      />
    </section>
  );
}

function ConnectionField({
  label,
  value,
  empty,
  mono,
}: {
  label: string;
  value: string | null;
  empty?: string | null;
  mono?: boolean;
}) {
  const isEmpty = value == null || value === "";
  return (
    <div className="bg-card px-3 py-2.5">
      <div className="font-sans text-xs font-medium tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-0.5 break-all text-sm",
          mono && "font-mono",
          isEmpty ? "text-muted-foreground/60 italic" : "text-foreground",
        )}
      >
        {isEmpty ? empty ?? "—" : value}
      </div>
    </div>
  );
}

function EditConnectionDialog({
  open,
  onOpenChange,
  accountId,
  initial,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  accountId: string;
  initial: {
    loginUrl: string;
    login: string;
    password: string;
    brokerServer: string;
  };
  onSaved: () => Promise<void>;
}) {
  const [loginUrl, setLoginUrl] = useState(initial.loginUrl);
  const [login, setLogin] = useState(initial.login);
  const [password, setPassword] = useState(initial.password);
  const [brokerServer, setBrokerServer] = useState(initial.brokerServer);
  const [busy, setBusy] = useState(false);

  // Re-seed inputs every time the dialog opens so an aborted edit
  // doesn't bleed into the next edit session.
  useEffect(() => {
    if (open) {
      setLoginUrl(initial.loginUrl);
      setLogin(initial.login);
      setPassword("");
      setBrokerServer(initial.brokerServer);
      setBusy(false);
    }
  }, [open, initial.loginUrl, initial.login, initial.brokerServer]);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Only send fields that actually changed. Password is special:
      // we never display it, so we only ship it when the operator
      // typed something. Empty string on loginUrl clears the override.
      const patch: {
        loginUrl?: string | null;
        login?: string;
        password?: string;
        brokerServer?: string | null;
      } = {};
      if (loginUrl.trim() !== initial.loginUrl.trim()) {
        patch.loginUrl = loginUrl.trim() === "" ? null : loginUrl.trim();
      }
      if (login.trim() !== initial.login.trim() && login.trim() !== "") {
        patch.login = login.trim();
      }
      if (password !== "") patch.password = password;
      if (brokerServer.trim() !== initial.brokerServer.trim()) {
        patch.brokerServer =
          brokerServer.trim() === "" ? null : brokerServer.trim();
      }
      if (Object.keys(patch).length === 0) {
        toast.message("Nothing changed");
        onOpenChange(false);
        return;
      }
      await patchAccountCredentials(accountId, patch);
      toast.success("Connection updated");
      await onSaved();
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Connection</DialogTitle>
          <DialogDescription>
            Empty Terminal URL → fall back to the firm default. Empty
            Password → keep the existing password. Other empty fields are
            left unchanged.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <FormField
            label="Terminal URL"
            hint="Web terminal the bot opens to log in. Leave empty to use the firm default."
          >
            <Input
              value={loginUrl}
              onChange={(e) => setLoginUrl(e.target.value)}
              autoComplete="off"
              placeholder="https://webtrader.example.com/terminal"
            />
          </FormField>
          <FormField label="Login">
            <Input
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              autoComplete="off"
              placeholder="Account login / number"
            />
          </FormField>
          <FormField label="Password" hint="Leave empty to keep the current password.">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="Replace existing password"
            />
          </FormField>
          <FormField
            label="Broker Server"
            hint="Only needed if the firm gave you a non-default server name."
          >
            <Input
              value={brokerServer}
              onChange={(e) => setBrokerServer(e.target.value)}
              autoComplete="off"
              placeholder="e.g. InstantFunding-Live"
            />
          </FormField>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="button" onClick={() => void submit()} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LatestSnapshotCard({
  account,
  currency,
  latest,
}: {
  account: AccountRow;
  currency: string;
  latest: AccountSnapshot | null;
}) {
  const terminal = account.outcome != null;
  const equity = terminal ? account.final_equity : latest?.equity ?? null;
  const balance = terminal ? account.final_balance : latest?.balance ?? null;
  const openPnl = terminal ? 0 : latest?.pnl_open ?? null;
  const asOf = terminal
    ? account.outcome_at
    : latest?.snapshot_at ?? null;

  return (
    <div className="space-y-3">
      {terminal && (
        <div
          className={cn(
            "flex items-center gap-3 rounded-lg border px-4 py-3 text-sm",
            account.outcome === "passed"
              ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-500"
              : "border-destructive/30 bg-destructive/5 text-destructive",
          )}
        >
          {account.outcome === "passed" ? (
            <Check className="h-4 w-4" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          <div className="flex-1">
            <div className="font-medium capitalize">Account {account.outcome}</div>
            <div className="mt-0.5 text-xs opacity-80">
              Marked {account.outcome_at ? new Date(account.outcome_at).toLocaleString() : "—"}. Bot
              has stopped monitoring this account; history below is frozen.
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-border/40 bg-border/40 sm:grid-cols-3">
        <SnapshotTile label="Equity" value={equity} currency={currency} mono tone="accent" />
        <SnapshotTile label="Balance" value={balance} currency={currency} mono />
        <SnapshotTile
          label={terminal ? "Open PnL (at close)" : "Open PnL"}
          value={openPnl}
          currency={currency}
          mono
          tone={openPnl != null && openPnl < 0 ? "destructive" : "emerald"}
        />
      </div>
      {asOf != null && (
        <div className="text-right text-[10px] uppercase tracking-wider text-muted-foreground">
          {terminal ? "Frozen" : "As of"} {new Date(asOf).toLocaleString()}
        </div>
      )}
    </div>
  );
}

function SnapshotTile({
  label,
  value,
  currency,
  mono,
  tone,
}: {
  label: string;
  value: number | null;
  currency: string;
  mono?: boolean;
  tone?: "accent" | "emerald" | "destructive";
}) {
  const color =
    tone === "accent"
      ? "text-accent"
      : tone === "emerald"
      ? "text-emerald-500"
      : tone === "destructive"
      ? "text-destructive"
      : "text-foreground";
  return (
    <div className="bg-card px-4 py-3">
      <div className="font-sans text-xs font-medium capitalize tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-1 text-sm font-semibold",
          mono && "font-mono",
          color,
        )}
      >
        {value == null ? `— ${currency}` : fmtMoney(value, currency)}
      </div>
    </div>
  );
}

/**
 * Per-account trade history table. Open positions are rows with a NULL
 * close timestamp — rendered with an "Open" pill and em-dash PnL.
 * Pair slug links back to the hedge view.
 */
function PositionHistoryTable({
  trades,
  currency,
}: {
  trades: AccountTrade[];
  currency: string;
}) {
  if (trades.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border/50 bg-muted/5 px-4 py-10 text-center text-xs text-muted-foreground">
        No trades yet — this account hasn&rsquo;t placed a position.
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-xl border border-border/40 bg-muted/5">
      <DataTableShell minWidth={820}>
        <DataTableHeader
          labels={["Opened", "Closed", "Symbol", "Side", "Lots", "Entry", "Close", "PnL", "Pair"]}
        />
        <tbody>
          {trades.map((t, i) => {
            const open = t.closed_at == null;
            return (
              <DataTableRow key={t.id} index={i}>
                <DataTableCell align="left" className="px-4 font-mono text-[11px] text-muted-foreground">
                  {fmtTs(t.opened_at)}
                </DataTableCell>
                <DataTableCell className="font-mono text-[11px] text-muted-foreground">
                  {open ? (
                    <span className="inline-flex rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-500">
                      Open
                    </span>
                  ) : (
                    fmtTs(t.closed_at)
                  )}
                </DataTableCell>
                <DataTableCell className="text-xs font-medium">{t.symbol}</DataTableCell>
                <DataTableCell>
                  <span
                    className={cn(
                      "rounded px-1.5 py-0.5 font-mono text-[10px] uppercase",
                      t.side === "buy"
                        ? "bg-emerald-500/10 text-emerald-500"
                        : "bg-destructive/10 text-destructive",
                    )}
                  >
                    {t.side}
                  </span>
                </DataTableCell>
                <DataTableCell className="font-mono text-xs">
                  {t.lots.toFixed(2)}
                </DataTableCell>
                <DataTableCell className="font-mono text-xs">
                  {t.entry_price.toFixed(5)}
                </DataTableCell>
                <DataTableCell className="font-mono text-xs">
                  {t.close_price != null ? t.close_price.toFixed(5) : "—"}
                </DataTableCell>
                <DataTableCell
                  className={cn(
                    "font-mono text-xs font-medium",
                    t.pnl == null
                      ? "text-muted-foreground"
                      : t.pnl >= 0
                        ? "text-emerald-500"
                        : "text-destructive",
                  )}
                >
                  {t.pnl == null ? "—" : fmtMoney(t.pnl, currency)}
                </DataTableCell>
                <DataTableCell>
                  {t.pair_slug ? (
                    <Link
                      to={`/pairs/${encodeURIComponent(t.pair_slug)}`}
                      className="inline-flex items-center rounded-full bg-accent/10 px-2 py-0.5 font-mono text-[10px] text-accent hover:bg-accent/20"
                    >
                      {t.pair_slug}
                    </Link>
                  ) : (
                    <span className="font-mono text-[10px] text-muted-foreground">—</span>
                  )}
                </DataTableCell>
              </DataTableRow>
            );
          })}
        </tbody>
      </DataTableShell>

      {/* Mobile: stacked cards (DataTableShell is hidden below lg) */}
      <ul className="flex flex-col gap-2 p-3 lg:hidden">
        {trades.map((t) => {
          const open = t.closed_at == null;
          return (
            <li
              key={t.id}
              className="flex flex-col gap-2 rounded-lg border border-border/40 bg-card p-3 text-xs"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-foreground">{t.symbol}</span>
                <span
                  className={cn(
                    "rounded px-1.5 py-0.5 font-mono text-[10px] uppercase",
                    t.side === "buy"
                      ? "bg-emerald-500/10 text-emerald-500"
                      : "bg-destructive/10 text-destructive",
                  )}
                >
                  {t.side}
                </span>
                {open ? (
                  <span className="ml-auto inline-flex rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-500">
                    Open
                  </span>
                ) : (
                  <span
                    className={cn(
                      "ml-auto font-mono text-xs font-medium",
                      t.pnl == null
                        ? "text-muted-foreground"
                        : t.pnl >= 0
                          ? "text-emerald-500"
                          : "text-destructive",
                    )}
                  >
                    {t.pnl == null ? "—" : fmtMoney(t.pnl, currency)}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
                <div>
                  <span className="block text-[10px] uppercase tracking-wider">Lots</span>
                  <span className="font-mono text-foreground">{t.lots.toFixed(2)}</span>
                </div>
                <div>
                  <span className="block text-[10px] uppercase tracking-wider">Entry</span>
                  <span className="font-mono text-foreground">{t.entry_price.toFixed(5)}</span>
                </div>
                <div>
                  <span className="block text-[10px] uppercase tracking-wider">Close</span>
                  <span className="font-mono text-foreground">
                    {t.close_price != null ? t.close_price.toFixed(5) : "—"}
                  </span>
                </div>
                <div>
                  <span className="block text-[10px] uppercase tracking-wider">Opened</span>
                  <span className="font-mono text-foreground">{fmtTs(t.opened_at)}</span>
                </div>
              </div>
              {t.pair_slug && (
                <Link
                  to={`/pairs/${encodeURIComponent(t.pair_slug)}`}
                  className="inline-flex w-fit items-center rounded-full bg-accent/10 px-2 py-0.5 font-mono text-[10px] text-accent hover:bg-accent/20"
                >
                  {t.pair_slug}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Every action an operator can take on a non-terminal account:
 *   - Advance Phase   (same profile dir — step1 → step2 → funded)
 *   - Resume          (only when status='paused')
 *   - Mark Passed     (terminal, drains partner too)
 *   - Mark Breached   (terminal, drains partner too)
 *
 * "Advance Phase" is the critical flow for passing step1 without
 * breaching the firm's device-fingerprint check: we keep the same
 * account id so the persistent Brave profile at
 * `profiles/<firmId>/<accountId>/` is reused across the re-login. Mark
 * Passed is the alternative: use it when there's no next-step account
 * coming (funded-phase complete) or the firm issues a new account id
 * for the next step.
 */
function LifecycleActions({
  accountId,
  accountSlug,
  assignedBotId,
  status,
  phase,
  steps,
  challengeType,
  platformType,
  currentAccountLabel,
  onDone,
}: {
  accountId: string;
  accountSlug: string;
  assignedBotId: string | null;
  status: string;
  phase: string;
  steps: number | null;
  challengeType: string | null;
  platformType: string;
  currentAccountLabel: string | null;
  onDone: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<"passed" | "breached" | "resume" | null>(null);

  const mark = useCallback(
    async (outcome: "passed" | "breached") => {
      if (busy) return;
      setBusy(outcome);
      try {
        if (outcome === "passed") {
          await markAccountPassed(accountId);
          toast.success("Account marked passed");
        } else {
          await markAccountBreached(accountId);
          toast.success("Account marked breached");
        }
        await onDone();
      } catch (e) {
        toast.error(extractApiErrorMessage(e));
      } finally {
        setBusy(null);
      }
    },
    [accountId, busy, onDone],
  );

  const resume = useCallback(async () => {
    if (busy) return;
    setBusy("resume");
    try {
      await resumeAccount(accountId);
      toast.success("Account resumed — bot is spinning up");
      await onDone();
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  }, [accountId, busy, onDone]);

  // Non-funded phases always pass by advancing with new firm-issued
  // creds. For 1-step programs that means step1 → funded; otherwise
  // step1 → step2 → funded. Funded is the last stop — "Mark Passed"
  // only makes sense there.
  const nextPhase = nextPhaseFor(phase, steps, challengeType);
  const canAdvance = nextPhase != null;
  const canMarkPassed = phase === "funded";
  const isInstant = steps === 0 || challengeType === "instant";
  const successActionLabel = isInstant ? "Record Payout" : "Mark Passed";
  const successConfirmTitle = isInstant ? "Record Instant Payout?" : "Mark Account Passed?";
  const successConfirmDescription = isInstant
    ? "Closes the instant funding account with a payout stamp. Bot drains immediately. Partner leg is paused (profile preserved). Terminal — no undo."
    : "Closes the funded account out with a pass stamp. Bot drains immediately. Partner leg is paused (profile preserved). Terminal — no undo.";

  return (
    <section className="space-y-4 rounded-xl border border-border/40 bg-muted/5 p-4 sm:p-5">
      <div>
        <h2 className="text-sm font-semibold text-foreground">Lifecycle</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {nextPhase
            ? `Passed this phase? Advance to ${phaseLabel(nextPhase)} with the new credentials — same account, same browser fingerprint, no device-change flag.`
            : isInstant
              ? "Payout received or account breached. Record the outcome to drain the bot, freeze history, and keep it categorized under Instant Funding."
              : "Challenge fully cleared. Mark passed to close the account out with a pass badge and freeze its history."}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {status === "paused" && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => void resume()}
            disabled={busy != null}
          >
            {busy === "resume" ? "Resuming…" : "Resume bot"}
          </Button>
        )}

        {canAdvance && (
          <AdvancePhaseButton
            accountId={accountId}
            nextPhase={nextPhase}
            platformType={platformType}
            currentAccountLabel={currentAccountLabel}
            disabled={busy != null}
            onDone={onDone}
          />
        )}

        {canMarkPassed && (
          <AlertDialog>
            <AlertDialogTrigger
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-500 transition-colors hover:bg-emerald-500/15 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={busy != null}
            >
              <Check className="h-3.5 w-3.5" />
              {successActionLabel}
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogMedia className="bg-emerald-500/10 text-emerald-500">
                  <Check />
                </AlertDialogMedia>
                <AlertDialogTitle>{successConfirmTitle}</AlertDialogTitle>
                <AlertDialogDescription>
                  {successConfirmDescription}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-emerald-500 text-white hover:bg-emerald-600"
                  onClick={() => void mark("passed")}
                >
                  {busy === "passed" ? "Marking…" : successActionLabel}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        <AlertDialog>
          <AlertDialogTrigger
            className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/15 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={busy != null}
          >
            <XIcon className="h-3.5 w-3.5" />
            Mark Breached
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogMedia className="bg-destructive/10 text-destructive">
                <AlertTriangle />
              </AlertDialogMedia>
              <AlertDialogTitle>Mark Account Breached?</AlertDialogTitle>
              <AlertDialogDescription>
                The account blew a rule (max DD, daily DD, consistency). Bot
                drains immediately. Partner is paused, not closed. Terminal
                — no undo.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                onClick={() => void mark("breached")}
              >
                {busy === "breached" ? "Marking…" : "Mark breached"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <ReassignBotDialog
          accountSlug={accountSlug}
          currentBotId={assignedBotId}
          onDone={onDone}
        />
      </div>
    </section>
  );
}

function nextPhaseFor(
  phase: string,
  steps: number | null,
  challengeType: string | null,
): "step2" | "funded" | null {
  if (phase === "step1") {
    if (steps === 1 || challengeType === "1-step") return "funded";
    return "step2";
  }
  if (phase === "step2") return "funded";
  return null;
}

function phaseLabel(phase: "step2" | "funded"): string {
  return phase === "step2" ? "Step 2" : "Funded";
}

/**
 * Opens the AdvancePhase dialog. Keeps state local so the parent's
 * busy spinner only shows the row-level actions (mark / resume).
 */
function AdvancePhaseButton({
  accountId,
  nextPhase,
  platformType,
  currentAccountLabel,
  disabled,
  onDone,
}: {
  accountId: string;
  nextPhase: "step2" | "funded";
  platformType: string;
  currentAccountLabel: string | null;
  disabled: boolean;
  onDone: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        size="sm"
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        Advance To {phaseLabel(nextPhase)}
      </Button>
      <AdvancePhaseDialog
        accountId={accountId}
        nextPhase={nextPhase}
        platformType={platformType}
        currentAccountLabel={currentAccountLabel}
        open={open}
        onOpenChange={setOpen}
        onDone={onDone}
      />
    </>
  );
}

/**
 * The actual credential-entry form. Submits creds → worker updates
 * the SAME account row in place + pushes account:removed → assigned
 * so the bot reloads on the preserved Brave profile directory.
 */
function AdvancePhaseDialog({
  accountId,
  nextPhase,
  platformType,
  currentAccountLabel,
  open,
  onOpenChange,
  onDone,
}: {
  accountId: string;
  nextPhase: "step2" | "funded";
  platformType: string;
  currentAccountLabel: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onDone: () => Promise<void>;
}) {
  // Match-Trader is one-login-many-accounts: the firm's dashboard URL +
  // email + password are shared. Advancing only mints a new account
  // number visible on the dashboard list — driver clicks that card via
  // cfg.accountLabel. Other platforms issue fresh per-phase creds.
  const isMatchTrader = platformType === "match-trader";

  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [brokerServer, setBrokerServer] = useState("");
  const [loginUrl, setLoginUrl] = useState("");
  const [accountLabel, setAccountLabel] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setLogin("");
      setPassword("");
      setBrokerServer("");
      setLoginUrl("");
      setAccountLabel("");
      setBusy(false);
    }
  }, [open]);

  const submit = async () => {
    if (busy) return;
    if (isMatchTrader) {
      if (accountLabel.trim() === "") {
        toast.error("New account number is required");
        return;
      }
    } else if (login.trim() === "" || password === "") {
      toast.error("Login and password are required");
      return;
    }
    setBusy(true);
    try {
      if (isMatchTrader) {
        await advanceAccountPhase(accountId, {
          phase: nextPhase,
          accountLabel: accountLabel.trim(),
        });
      } else {
        await advanceAccountPhase(accountId, {
          phase: nextPhase,
          login: login.trim(),
          password,
          brokerServer: brokerServer.trim() === "" ? null : brokerServer.trim(),
          loginUrl: loginUrl.trim() === "" ? null : loginUrl.trim(),
        });
      }
      toast.success(
        `Advanced to ${nextPhase === "step2" ? "Step 2" : "Funded"} — bot is re-logging in on the same profile`,
      );
      onOpenChange(false);
      await onDone();
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Advance To {nextPhase === "step2" ? "Step 2" : "Funded"}
          </DialogTitle>
          <DialogDescription>
            {isMatchTrader
              ? "Match-Trader keeps one firm dashboard login for every account. Just paste the new account number from the dashboard's account list — the bot picks that card and continues on the same browser profile."
              : "Enter the next-phase credentials the firm issued. The account id and browser profile stay the same — same canvas fingerprint, same cookies, same device. No device-change flag from the firm's side."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          {isMatchTrader ? (
            <FormField
              label="New Account Number"
              hint={
                currentAccountLabel
                  ? `Current: ${currentAccountLabel}. Use the exact number/label as shown on the firm dashboard.`
                  : "Use the exact number/label as shown on the firm dashboard."
              }
            >
              <Input
                value={accountLabel}
                onChange={(e) => setAccountLabel(e.target.value)}
                autoComplete="off"
                placeholder="e.g. 6001234"
              />
            </FormField>
          ) : (
            <>
              <FormField label="Login">
                <Input
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  autoComplete="off"
                  placeholder="New account login"
                />
              </FormField>
              <FormField label="Password">
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder="New password"
                />
              </FormField>
              <FormField
                label="Broker Server"
                hint="Only needed if it changed (most MT5 firms reuse the same server)."
              >
                <Input
                  value={brokerServer}
                  onChange={(e) => setBrokerServer(e.target.value)}
                  autoComplete="off"
                  placeholder="Leave empty to keep current"
                />
              </FormField>
              <FormField
                label="Login URL"
                hint="Only needed if the firm gave you a different terminal URL for this phase."
              >
                <Input
                  value={loginUrl}
                  onChange={(e) => setLoginUrl(e.target.value)}
                  autoComplete="off"
                  placeholder="Leave empty to keep current"
                />
              </FormField>
            </>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="button" onClick={() => void submit()} disabled={busy}>
            {busy ? "Advancing…" : `Advance to ${nextPhase}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium capitalize tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
      {hint && <div className="text-[11px] text-muted-foreground/80">{hint}</div>}
    </div>
  );
}

/**
 * Inline nickname editor — a pencil pill next to the account header.
 * Click to edit, Enter/blur to save, Esc to cancel. Empty string clears.
 */
function LabelEditor({
  accountId,
  initial,
  onSaved,
}: {
  accountId: string;
  initial: string | null;
  onSaved: (label: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initial ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setValue(initial ?? "");
  }, [editing, initial]);

  const save = useCallback(async () => {
    if (saving) return;
    const trimmed = value.trim();
    const normalized = trimmed === "" ? null : trimmed;
    if (normalized === initial) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const res = await setAccountLabel(accountId, normalized);
      onSaved(res.accountLabel);
      setEditing(false);
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }, [accountId, initial, onSaved, saving, value]);

  if (editing) {
    return (
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => void save()}
        onKeyDown={(e) => {
          if (e.key === "Enter") void save();
          if (e.key === "Escape") {
            setValue(initial ?? "");
            setEditing(false);
          }
        }}
        disabled={saving}
        maxLength={64}
        placeholder="Nickname"
        className="h-6 rounded-md border border-accent/40 bg-background px-2 text-xs outline-none focus:ring-1 focus:ring-accent/40"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={cn(
        "inline-flex h-6 items-center rounded-md border px-2 text-[11px] transition-colors",
        initial
          ? "border-accent/30 bg-accent/5 text-accent hover:bg-accent/10"
          : "border-dashed border-border/50 text-muted-foreground hover:text-foreground",
      )}
    >
      {initial ?? "+ Nickname"}
    </button>
  );
}

function fmtTs(ms: number | null): string {
  if (ms == null) return "—";
  const d = new Date(ms);
  return `${d.toLocaleDateString(undefined, { month: "short", day: "2-digit" })} ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
}

/**
 * Kicks the bot to rescrape the platform's History tab. The worker
 * writes new rows into D1 asynchronously — we poll `getAccountTrades`
 * for up to ~20s after the request to pick up the rows as they land.
 * Poll stops early once the trade count grows or the budget expires.
 */
function RefetchHistoryButton({
  accountId,
  onRefetched,
}: {
  accountId: string;
  onRefetched: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);

  const run = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      await refetchAccountHistory(accountId);
      toast.success("Refetch requested — bot is pulling platform history");
      // Poll for backfilled rows. Each onRefetched call pulls the
      // latest trades; BotChannel INSERTs happen as soon as the bot
      // streams the result back so 2-3 s is usually enough for the
      // first rows to appear, but we stretch the budget to 20 s to
      // cover slow History-tab renders on larger accounts.
      const deadline = Date.now() + 20_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2_000));
        await onRefetched();
      }
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }, [accountId, busy, onRefetched]);

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => void run()}
      disabled={busy}
    >
      {busy ? "Refetching…" : "Refetch From Broker"}
    </Button>
  );
}

function fmtMoney(n: unknown, currency: string): string {
  const num = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(num)) return `— ${currency}`;
  return `${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}
